import type { Topic, EntityType } from '@communities/content-management/domain';
import { useCommunityTopics, useDeleteCommunityTopic } from '@communities/content-management/hooks';
import { DataList, type TableColumn, type DeleteItemContext, SectionHeader } from '@core/components';
import { Badge, BadgeVariant, Section } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo } from 'react';

import { TopicDialog } from '../TopicDialog';

interface StrikeTopicsSectionProps {
  communityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  /** Optional custom section title. Defaults to "Topics" */
  sectionTitle?: string;
}

/**
 * Section for configuring which topics count towards the strike system.
 * Displays a DataList of topics with their strike-enabled status.
 * Topics are created in the Classification tab — this section only configures the strike flag.
 */
export function StrikeTopicsSection({ communityId, entityType, isEntityAdmin, sectionTitle = 'Topics' }: StrikeTopicsSectionProps) {
  const { topics, loading, errors } = useCommunityTopics(communityId);
  const { deleteTopic, loading: deleting, errors: deleteErrors, resetErrors } = useDeleteCommunityTopic(communityId);

  const [selectedTopic, setSelectedTopic] = useState<Topic | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const allErrors = useMemo(() => {
    const combined = [...(errors || []), ...(deleteErrors || [])];

    return combined.length > 0 ? combined : null;
  }, [errors, deleteErrors]);

  const columns: TableColumn<Topic>[] = [
    {
      key: 'name',
      header: 'TOPIC NAME',
      render: topic => topic.name,
      width: '3fr',
    },
    {
      key: 'strikes',
      header: 'STRIKES',
      render: topic => (
        <Badge text={topic.isStrikeEnabled ? 'ENABLED' : 'DISABLED'} variant={topic.isStrikeEnabled ? BadgeVariant.Success : BadgeVariant.Default} />
      ),
      width: '1fr',
    },
  ];

  const handleEdit = useCallback(
    (topic: Topic) => {
      setSelectedTopic(topic);
      setIsDialogOpen(true);
      resetErrors();
    },
    [resetErrors]
  );

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedTopic(undefined);
    resetErrors();
  }, [resetErrors]);

  const deleteItemContext: DeleteItemContext<Topic> | undefined = isEntityAdmin
    ? {
        handler: async (item: Topic) => await deleteTopic(communityId, item.name),
        isDeleting: deleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader description="Configure which topics should count towards strike system." title={`${sectionTitle} (${topics.length})`} />

        <DataList
          columns={columns}
          data={topics}
          dataTypeName="topic"
          deleteItemContext={deleteItemContext}
          error={allErrors}
          isLoading={loading}
          onEdit={isEntityAdmin ? handleEdit : undefined}
        />
      </Section>

      <TopicDialog
        editMode="strike"
        entityId={communityId}
        entityType={entityType}
        isOpen={isDialogOpen}
        topic={selectedTopic || undefined}
        onClose={handleCloseDialog}
      />
    </>
  );
}
